import request from '@/http/request'

// 实体关系分析工作流配置
const ENTITY_WORKFLOW_ID = '7569507891061735474'
const COZE_API_URL = 'https://api.coze.cn/v1/workflow/run'
const COZE_TOKEN_URL = '/ai/coze/get-online'

/**
 * 获取Coze token
 * @returns {Promise<string>} token
 */
async function getCozeToken() {
  try {
    const response = await fetch(COZE_TOKEN_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authorization')
      }
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`获取token失败: ${response.status} ${response.statusText}, 详情: ${errorText}`)
    }

    const data = await response.json()
    
    if (data && data.success && data.data && data.data.access_token) {
      return data.data.access_token
    } else {
      throw new Error('获取token失败：返回数据格式错误')
    }
  } catch (error) {
    throw error
  }
}

/**
 * 调用实体关系分析工作流
 * @param {string} content - 文档内容
 * @returns {Promise} 工作流结果
 */
async function callEntityAnalysisWorkflow(content, retryCount = 0) {
  try {
    const token = await getCozeToken()
    
    const requestData = {
      workflow_id: ENTITY_WORKFLOW_ID,
      parameters: {
        input: content
      }
    }
    
    const response = await fetch(COZE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestData)
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Coze API请求失败: ${response.status} ${response.statusText}, 详情: ${errorText}`)
    }
    
    const data = await response.json()
    
    if (data.code === 0 && data.data) {
      return data.data
    } else {
      const errorMsg = data.msg || data.message || '工作流执行失败'
      const errorCode = data.code || '未知错误码'
      throw new Error(`调用工作流失败 (code: ${errorCode}): ${errorMsg}`)
    }
    
  } catch (error) {
    // 系统异常重试机制
    const maxRetries = 2
    if (error.message.includes('5000') && retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 5000))
      return await callEntityAnalysisWorkflow(content, retryCount + 1)
    }
    
    throw error
  }
}

/**
 * 强健的JSON解析函数
 * @param {string} jsonString - 需要解析的JSON字符串
 * @returns {Object} 解析后的对象
 */
function robustJSONParse(jsonString) {
  // 尝试多种清理策略
  const cleaningStrategies = [
    // 策略1: 直接解析
    (str) => str,
    
    // 策略2: 清理转义字符
    (str) => str.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\'),
    
    // 策略3: 移除多余的引号和转义
    (str) => str.replace(/\\"/g, '"').replace(/\n/g, '\\n'),
    
    // 策略4: 处理双重转义
    (str) => str.replace(/\\\\\"/g, '"').replace(/\\\\\\\\/g, '\\'),
    
    // 策略5: 提取纯JSON（去掉可能的前后缀）
    (str) => {
      const match = str.match(/\{[\s\S]*\}/)
      return match ? match[0] : str
    }
  ]
  
  for (let i = 0; i < cleaningStrategies.length; i++) {
    try {
      const cleanedStr = cleaningStrategies[i](jsonString)
      return JSON.parse(cleanedStr)
    } catch (error) {
      console.log(`JSON解析策略 ${i + 1} 失败:`, error.message)
      if (i === cleaningStrategies.length - 1) {
        throw error
      }
    }
  }
}

/**
 * 解析工作流返回的实体分析结果
 * @param {Object} result - 工作流返回的原始结果
 * @returns {Object} 解析后的结构化结果
 */
function parseEntityAnalysisResult(result) {
  try {
    console.log('原始工作流结果:', result)
    
    // 假设工作流返回的格式为 { output: "```json\n{...}\n```" }
    let outputContent = result.output || result
    console.log('提取的输出内容:', outputContent)
    
    // 如果outputContent是字符串，先尝试解析它
    if (typeof outputContent === 'string') {
      try {
        // 先尝试解析成对象（可能是JSON字符串包含了output字段）
        const parsed = JSON.parse(outputContent)
        if (parsed.output && typeof parsed.output === 'string') {
          outputContent = parsed.output
          console.log('解析后的output字段:', outputContent)
        }
      } catch (e) {
        // 如果解析失败，继续使用原始字符串
      }
    }
    
    // 如果是对象且有output字段，继续提取
    if (typeof outputContent === 'object' && outputContent.output) {
      outputContent = outputContent.output
      console.log('从对象中提取的output字段:', outputContent)
    }
    
    if (typeof outputContent === 'string') {
      // 提取JSON内容，去掉markdown代码块标记
      const jsonMatch = outputContent.match(/```json\s*([\s\S]*?)\s*```/)
      if (jsonMatch && jsonMatch[1]) {
        let jsonContent = jsonMatch[1].trim()
        console.log('提取的JSON内容（前200字符）:', jsonContent.substring(0, 200))
        outputContent = robustJSONParse(jsonContent)
        console.log('markdown解析成功')
      } else {
        // 如果没有找到markdown格式，直接尝试强健解析
        console.log('没有找到markdown格式，直接解析')
        outputContent = robustJSONParse(outputContent)
        console.log('直接解析成功')
      }
    }
    
    // 验证必要的字段
    const entities = outputContent.entities || []
    const relationships = outputContent.relationships || []
    const events = outputContent.events || []
    
    console.log('解析结果:', { 
      entitiesCount: entities.length, 
      relationshipsCount: relationships.length, 
      eventsCount: events.length 
    })
    
    return {
      entities,
      relationships,
      events,
      original: result
    }
  } catch (error) {
    console.error('解析实体分析结果失败:', error)
    console.error('错误的内容:', typeof outputContent === 'string' ? outputContent.substring(0, 200) : outputContent)
    throw new Error('解析实体分析结果失败: ' + error.message)
  }
}

/**
 * 执行实体关系分析
 * @param {string} content - 文档内容
 * @returns {Promise<Object>} 分析结果
 */
export async function analyzeEntitiesAndRelationships(content) {
  try {
    if (!content || !content.trim()) {
      throw new Error('文档内容不能为空')
    }
    
    // 清理HTML标签，只保留文本内容
    const textContent = content.replace(/(<([^>]+)>)/gi, '').trim()
    
    if (!textContent) {
      throw new Error('文档没有有效的文本内容')
    }
    
    console.log('开始实体关系分析，内容长度:', textContent.length)
    
    // 调用工作流
    const result = await callEntityAnalysisWorkflow(textContent)
    console.log('工作流原始返回结果:', result)
    
    // 解析结果
    const parsedResult = parseEntityAnalysisResult(result)
    console.log('解析后的结果:', parsedResult)
    
    return parsedResult
    
  } catch (error) {
    console.error('实体关系分析失败:', error)
    throw error
  }
}

/**
 * 创建加载提示模态框
 */
export function createEntityAnalysisLoadingModal() {
  const loadingHtml = `
    <div id="entity-analysis-loading-modal" style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10001;
    ">
      <div style="
        background: white;
        border-radius: 12px;
        padding: 30px;
        text-align: center;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        max-width: 400px;
        width: 90%;
      ">
        <div style="
          width: 50px;
          height: 50px;
          border: 4px solid #e3f2fd;
          border-top: 4px solid #2196f3;
          border-radius: 50%;
          margin: 0 auto 20px;
          animation: spin 1s linear infinite;
        "></div>
        <h3 style="
          margin: 0 0 10px 0;
          color: #2196f3;
          font-size: 18px;
          font-weight: bold;
        ">🔍 AI 正在分析实体关系...</h3>
        <p style="
          margin: 0;
          color: #666;
          font-size: 14px;
          line-height: 1.5;
        ">正在识别文档中的实体、关系和事件<br/>请耐心等待1-2分钟</p>
        <div style="
          margin-top: 20px;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 6px;
          border-left: 3px solid #2196f3;
          font-size: 12px;
          color: #666;
        ">
          💡 提示：分析完成后将跳转到白板页面展示结果
        </div>
      </div>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
  `
  
  document.body.insertAdjacentHTML('beforeend', loadingHtml)
}

/**
 * 移除加载提示模态框
 */
export function removeEntityAnalysisLoadingModal() {
  const modal = document.getElementById('entity-analysis-loading-modal')
  if (modal) {
    modal.remove()
  }
}
